import React, { useEffect, useState } from "react";
import { find } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { useDebounce } from "@lkf/hooks";
import PanelComponent from "@ui-kit/popover/panel";
import SearchPanel from "@radarly/components/filterbar/panels/search";
import { useQueryValidity } from "@radarly/core/queries/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Clause } from "@radarly/models/clause";
import { isClauseEmpty, isQueryClause } from "@radarly/models/clause/is";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { FullFacet } from "@radarly/models/facets";

interface IProps {
  clause: Clause;
  setClause: (clause: Clause) => void;
  hasTitle?: boolean;
  facet: FullFacet;
  onApply?: () => void;
  visible?: boolean;
  onCancel?: () => void;
  validateLabel?: string;
  className?: string;
}

function getQueryValue(clause: Clause) {
  if (clause && clause.and) {
    const queryClause = find(clause.and, isQueryClause);
    return queryClause ? "" + (queryClause.query || "") : "";
  } else {
    return "";
  }
}

function useQueryError(query: string) {
  const queryInfo = useQueryValidity(query);
  return { query: queryInfo.query, error: !queryInfo.isValidQuerySyntax };
}

export default function SearchPanelContainer({
  clause,
  setClause,
  hasTitle,
  facet,
  onApply,
  visible,
  onCancel,
  validateLabel,
  className,
}: IProps) {
  const locale = useCurrentUserLocale();
  const [tempSearch, setTempSearch] = useState(getQueryValue(clause));

  const debouncedSearch = useDebounce(tempSearch, 750);
  const { query, error } = useQueryError(debouncedSearch);

  useEffect(() => {
    if (query !== "" && !error) {
      setClause(
        refineClauseWithInnerClause(clause, {
          [Facet.Query]: query,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, query]);

  useEffect(() => {
    if (visible) {
      setTempSearch(getQueryValue(clause));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!visible && error) {
      setClause(
        refineClauseWithInnerClause(clause, {
          [Facet.Query]: "",
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, error]);

  const onClear = () => {
    setTempSearch("");
    setClause(
      refineClauseWithInnerClause(clause, {
        [Facet.Query]: null,
      })
    );
  };

  return (
    <PanelComponent
      locale={locale}
      onClear={onClear}
      hasTitle={hasTitle}
      title={tr(locale, "FILTERBAR::PANEL::FILTERBY", {
        filter: facet.label,
      })}
      onApply={onApply}
      disabled={query === "" || error || (onCancel && isClauseEmpty(clause))}
      onCancel={onCancel}
      validateLabel={validateLabel}
      className={className}
    >
      <SearchPanel
        search={tempSearch}
        onSearch={setTempSearch}
        locale={locale}
        error={error}
        onEnter={onApply}
        visible={visible}
      />
    </PanelComponent>
  );
}
